import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Snackbar,
  Alert,
  FormHelperText,
  IconButton,
  Chip,
  Tooltip,
  Divider,
  Container,
  Pagination
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LoginIcon from '@mui/icons-material/Login';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import config from '../config';
import { useNavigate } from 'react-router-dom';
import WorkHoursEditor from '../components/WorkHoursEditor';



const Users = () => {
  const { getToken, user: currentUser, isAuthenticated, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'sales',
    viewAllRecords: false,
    workHours: null,
    employment_type: 'hourly',
    base_rate: '',
    commission_rate: '',
    monthly_target: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalUsers: 0,
    totalPages: 1
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchUsers(pagination.page, pagination.limit);
    }
  }, [isAuthenticated, pagination.page, pagination.limit]);

  const fetchUsers = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await axios.get(`${config.apiUrl}/api/users?page=${page}&limit=${limit}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setUsers(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
      
      // Don't use mock data anymore, let the server response come through
      // If there's an error, just show the error message
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleWorkHoursChange = (newWorkHours) => {
    setFormData({
      ...formData,
      workHours: newWorkHours
    });
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    // Only require password for new users
    if (!openEditDialog && !formData.password) {
      errors.password = 'Password is required for new users';
    } else if (!openEditDialog && formData.password && formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    
    if (!formData.role) {
      errors.role = 'Role is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      const token = await getToken();
      
      if (openEditDialog && selectedUser) {
        // Update existing user
        const response = await axios.put(
          `${config.apiUrl}/api/users/${selectedUser.id}`,
          {
            name: formData.name,
            email: formData.email,
            role: formData.role,
            viewAllRecords: formData.viewAllRecords,
            workHours: formData.workHours,
            employment_type: formData.employment_type || 'hourly',
            base_rate: formData.base_rate !== '' ? parseFloat(formData.base_rate) : null,
            commission_rate: formData.commission_rate !== '' ? parseFloat(formData.commission_rate) / 100 : null,
            monthly_target: formData.monthly_target !== '' ? parseFloat(formData.monthly_target) : null,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        if (response.data.success) {
          // Update the user in the state
          setUsers(users.map(u => u.id === selectedUser.id ? response.data.data : u));
          
          // Show appropriate message based on Auth0 status
          let severity = 'success';
          let message = response.data.message || 'User updated successfully';
          
          if (response.data.auth0UpdateFailed) {
            severity = 'info'; // Use info instead of warning for better UX
            message = 'User updated successfully! (Auth0 sync will be handled automatically)';
            // Log technical details for developers
            console.info('Auth0 sync note:', response.data.technicalDetails);
          }
          
          setSnackbar({
            open: true,
            message: message,
            severity: severity
          });
          
          handleCloseDialog();
          
          // Reload users immediately to ensure the data is fresh
          fetchUsers(pagination.page, pagination.limit);
        } else {
          setSnackbar({
            open: true,
            message: response.data.message || 'Failed to update user',
            severity: 'error'
          });
        }
      } else {
        // Create new user
        const response = await axios.post(
          `${config.apiUrl}/api/users`,
          {
            name: formData.name,
            email: formData.email,
            password: formData.password,
            role: formData.role,
            viewAllRecords: formData.viewAllRecords
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        if (response.data.success) {
          // Add the new user to the state
          setUsers([...users, response.data.data]);
          
          setSnackbar({
            open: true,
            message: 'User created successfully',
            severity: 'success'
          });
          
          handleCloseDialog();
          
          // Reload users immediately to ensure the data is fresh
          fetchUsers(pagination.page, pagination.limit);
        } else {
          setSnackbar({
            open: true,
            message: response.data.message || 'Failed to create user',
            severity: 'error'
          });
        }
      }
    } catch (error) {
      console.error('Error saving user:', error);
      
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'An error occurred while saving the user',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    
    // Parse workHours if it's a string (from JSON column)
    let parsedWorkHours = null;
    if (user.workHours) {
      try {
        parsedWorkHours = typeof user.workHours === 'string' 
          ? JSON.parse(user.workHours) 
          : user.workHours;
      } catch (e) {
        console.error('Error parsing workHours:', e);
        parsedWorkHours = null;
      }
    }
    
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      viewAllRecords: user.viewAllRecords === 1,
      workHours: parsedWorkHours,
      employment_type: user.employment_type || 'hourly',
      base_rate: user.base_rate || '',
      commission_rate: user.commission_rate != null ? (parseFloat(user.commission_rate) * 100).toFixed(2) : '',
      monthly_target: user.monthly_target != null ? user.monthly_target : '',
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleLoginAs = (targetUser) => {
    startImpersonation(targetUser);
    navigate('/app');
  };

  const handleDeleteUser = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await axios.delete(`${config.apiUrl}/api/users/${selectedUser.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        // Remove the user from the state
        setUsers(users.filter(u => u.id !== selectedUser.id));
        
        setSnackbar({
          open: true,
          message: 'User deleted successfully',
          severity: 'success'
        });
        
        handleCloseDialog();
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || 'Failed to delete user',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'An error occurred while deleting the user',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
    }
  };

  const handleAddClick = () => {
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'sales',
      viewAllRecords: false,
      workHours: null,
      employment_type: 'hourly',
      base_rate: '',
      commission_rate: '',
      monthly_target: '',
    });
    setFormErrors({});
    setOpenAddDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'sales',
      viewAllRecords: false,
      workHours: null,
      employment_type: 'hourly',
      base_rate: '',
      commission_rate: '',
      monthly_target: '',
    });
    setFormErrors({});
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };



  const handleChangePage = (event, value) => {
    setPagination({
      ...pagination,
      page: value
    });
  };



  const roleToChipColor = (role) => {
    switch(role?.toLowerCase()) {
      case 'admin':
        return 'error';
      case 'manager':
        return 'warning';
      case 'sales':
        return 'success';
      case 'designer':
        return 'info';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role) => {
    // Map database roles to display values
    switch(role?.toLowerCase()) {
      case 'admin':
        return 'Administrator';
      case 'manager':
        return 'Manager';
      case 'sales':
        return 'Sales';
      case 'designer':
        return 'Designer';
      default:
        return role || 'Unknown';
    }
  };

  return (
    <Container maxWidth={false}>
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            User Management
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
          >
            Add User
          </Button>
        </Box>

        <Paper sx={{ width: '100%', mb: 3 }}>
          {loading && users.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 2 }}>
              {error}
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>View All Records</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip 
                            label={getRoleLabel(user.role)} 
                            color={roleToChipColor(user.role)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {user.viewAllRecords === 1 ? 
                            <CheckCircleIcon color="success" fontSize="small" /> : 
                            <ErrorIcon color="disabled" fontSize="small" />
                          }
                        </TableCell>
                        <TableCell align="right">
                          {(user.role || '').toLowerCase() !== 'admin' && user.id !== currentUser?.id && (
                            <Tooltip title="View as this user">
                              <IconButton
                                color="secondary"
                                onClick={() => handleLoginAs(user)}
                                size="small"
                              >
                                <LoginIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit User">
                            <IconButton
                              color="primary"
                              onClick={() => handleEditClick(user)}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete User">
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(user)}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <Pagination 
                    count={pagination.totalPages} 
                    page={pagination.page} 
                    onChange={handleChangePage} 
                    color="primary" 
                  />
                </Box>
              )}
            </>
          )}
        </Paper>

        {/* Add User Dialog */}
        <Dialog open={openAddDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Add New User</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Full Name"
                name="name"
                autoFocus
                value={formData.name}
                onChange={handleInputChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="password"
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                error={!!formErrors.password}
                helperText={formErrors.password}
              />
              <FormControl fullWidth margin="normal" error={!!formErrors.role}>
                <InputLabel id="role-label">Role</InputLabel>
                <Select
                  labelId="role-label"
                  id="role"
                  name="role"
                  value={formData.role}
                  label="Role"
                  onChange={handleInputChange}
                >
                  <MenuItem value="sales">Sales</MenuItem>
                  <MenuItem value="designer">Designer</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="admin">Administrator</MenuItem>
                </Select>
                {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel id="viewAllRecords-label">View All Records</InputLabel>
                <Select
                  labelId="viewAllRecords-label"
                  id="viewAllRecords"
                  name="viewAllRecords"
                  value={formData.viewAllRecords}
                  label="View All Records"
                  onChange={handleInputChange}
                >
                  <MenuItem value={false}>No</MenuItem>
                  <MenuItem value={true}>Yes</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              onClick={handleSubmit}
              disabled={!formData.name || !formData.email || !formData.password || !formData.role}
            >
              Add User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={openEditDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>Edit User</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Full Name"
                name="name"
                autoFocus
                value={formData.name}
                onChange={handleInputChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
              <FormControl fullWidth margin="normal" error={!!formErrors.role}>
                <InputLabel id="role-label">Role</InputLabel>
                <Select
                  labelId="role-label"
                  id="role"
                  name="role"
                  value={formData.role}
                  label="Role"
                  onChange={handleInputChange}
                >
                  <MenuItem value="sales">Sales</MenuItem>
                  <MenuItem value="designer">Designer</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="admin">Administrator</MenuItem>
                </Select>
                {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel id="viewAllRecords-label">View All Records</InputLabel>
                <Select
                  labelId="viewAllRecords-label"
                  id="viewAllRecords"
                  name="viewAllRecords"
                  value={formData.viewAllRecords}
                  label="View All Records"
                  onChange={handleInputChange}
                >
                  <MenuItem value={false}>No</MenuItem>
                  <MenuItem value={true}>Yes</MenuItem>
                </Select>
              </FormControl>
              
              <Divider sx={{ my: 3 }} />
              
              <WorkHoursEditor 
                workHours={formData.workHours}
                onChange={handleWorkHoursChange}
              />

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Payroll (Admin Only)
              </Typography>

              <FormControl fullWidth margin="normal">
                <InputLabel id="employment-type-label">Employment Type</InputLabel>
                <Select
                  labelId="employment-type-label"
                  name="employment_type"
                  value={formData.employment_type || 'hourly'}
                  label="Employment Type"
                  onChange={handleInputChange}
                >
                  <MenuItem value="hourly">Hourly</MenuItem>
                  <MenuItem value="salaried">Salaried</MenuItem>
                  <MenuItem value="commission_only">Commission Only</MenuItem>
                </Select>
              </FormControl>

              {formData.employment_type !== 'commission_only' && (
                <TextField
                  margin="normal"
                  fullWidth
                  label={formData.employment_type === 'salaried' ? 'Weekly Salary ($)' : 'Hourly Rate ($/hr)'}
                  name="base_rate"
                  type="number"
                  value={formData.base_rate}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={formData.employment_type === 'salaried' ? 'Fixed weekly amount (before tax)' : 'Rate per hour worked'}
                />
              )}

              {(formData.employment_type === 'commission_only' || formData.employment_type === 'salaried') && (
                <TextField
                  margin="normal"
                  fullWidth
                  label="Commission Rate (%)"
                  name="commission_rate"
                  type="number"
                  value={formData.commission_rate}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  helperText="Percentage of paid order value (e.g. 5 = 5%)"
                />
              )}

              {formData.role === 'sales' && (
                <TextField
                  margin="normal"
                  fullWidth
                  label="Monthly Sales Target ($)"
                  name="monthly_target"
                  type="number"
                  value={formData.monthly_target}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, step: 100 }}
                  helperText="Monthly invoiced target — used for commission calculation"
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              onClick={handleSubmit}
              disabled={!formData.name || !formData.email || !formData.role}
            >
              Update User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the user {selectedUser?.name}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleDeleteUser} 
              color="error" 
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default Users; 